/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  HalfContetntLayout,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import CustomGrid from 'components/grids/CustomGrid';
import { ComboBox } from 'components/selects/ComboBox';

type Props = {
  open: boolean;
  close: () => void;
  condition: ConditionProps;
};

interface ConditionProps {
  prdnProcCd: string;
  prdnProcCdNm?: string;
  bildPlntCd: string;
  bildPlntCdNm?: string;
  bldoCd?: string;
}

/**
 * 전극/셀 라인 선택 팝업
 */
const EltrCellLineInqPopup = ({ open, close, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const gridRef = useRef<any>();
  const [bldoCdList, setBldoCdList] = useState([]);
  const [condition, setCondition] = useState<ConditionProps>(props.condition);
  const [setCheckedItems, setSetCheckedItems] = useState<any>([]);
  const [rowData, setRowData] = useState<any>([]);

  const layoutDefinition = [
    {
      header: String(t('com.label.공정', '공정')),
      binding: 'userId',
      align: 'left',
      width: 150,
    },
    {
      binding: 'empNm',
      header: String(t('com.label.플랜트', '플랜트')),
      width: 120,
    },
    {
      binding: 'deptNm',
      header: String(t('com.label.동', '동')),
      align: 'left',
      width: 120,
    },
    {
      binding: 'deptNm',
      header: String(t('com.label.호기명', '호기명')),
      align: 'left',
      width: 120,
    },
    {
      binding: 'deptNm',
      header: String(t('com.label.설비명', '설비명')),
      align: 'left',
      width: 120,
    },
    {
      binding: 'deptNm',
      header: String(t('com.label.설비대수(대)', '설비대수(대)')),
      align: 'left',
      width: 120,
    },
  ];

  const initCodes = async () => {
    // const bldoList = getBldoCdList();
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = () => {
    //
  };

  const handleAddRow = () => {
    //
  };

  const handleDelRow = () => {
    //
  };

  const handleConfirm = () => {
    //
  };

  const handleSearch = () => {
    //
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  useEffect(() => {
    initCodes();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('setup.label.전극/셀 라인 추가', '전극/셀 라인 추가'))}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="popup">
              <SearchCols>
                <label>{t('com.label.공정', '공정')}</label>
                <CustomInputWithSearch
                  name="prdnProcCdNm"
                  value={condition?.prdnProcCdNm}
                  readOnly
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.플랜트', '플랜트')}</label>
                <CustomInputWithSearch
                  name="bildPlntCdNm"
                  value={condition?.bildPlntCdNm}
                  readOnly
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.동', '동')}</label>
                <ComboBox
                  classNameWrap="grow"
                  options={bldoCdList}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition?.bldoCd}
                  onChange={(value) => handleOnChange('bldoCd', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={{
              userId: '',
              empNm: '',
              deptNm: '',
              useYn: 'Y',
            }}
            setReset={setCondition}
            onSearch={handleSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <HalfContetntLayout style={{ marginTop: '24px' }}>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.생산라인 목록', '생산라인 목록')}</h3>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="lineGrid"
            layoutDefinition={layoutDefinition}
            rowData={rowData}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={onInitialized}
            onChangeCheckedItem={(items) => setCheckedItems(items)}
          />
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup></SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.label.삭제', '삭제')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            id="selectedLineGrid"
            layoutDefinition={layoutDefinition}
            rowData={rowData}
            height={500}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            allowPinning={false}
            align="center"
            initialized={onInitialized}
            onChangeCheckedItem={(items) => setCheckedItems(items)}
          />
        </div>
      </HalfContetntLayout>
    </CustomDialog>
  );
};

export default EltrCellLineInqPopup;
